import {
  BdcAdjustPreview,
  BdcDeliveryItemRequest,
  BdcShipmentDetail,
  BdcShipmentItem,
  BdcShipmentStore,
} from "../../../types/bdc.type";
import { compareLineNo, lineText } from "../format";

/**
 * ยอดส่ง (delivery) helpers — PO pieces against what is actually delivered, the auto spread's
 * input check, and the manual editor's draft. Pure, so the rules are tested without a screen.
 */

/** A store's delivered pieces of one barcode; a pre-1.5 API (no delivery_qty) delivers the PO. */
export const deliveredQty = (store: BdcShipmentStore, barcode: string): number =>
  store.delivery_qty?.[barcode] ?? store.qty?.[barcode] ?? 0;

export const itemDelivered = (item: BdcShipmentItem): number => item.delivery_qty ?? item.qty;

/**
 * The stores with `qty` replaced by the delivered pieces — what the control sheets print
 * (plan §6.2: ใบคุมใช้ยอดส่ง).
 */
export const deliveredStores = (stores: BdcShipmentStore[]): BdcShipmentStore[] =>
  stores.map((store) => (store.delivery_qty ? { ...store, qty: store.delivery_qty } : store));

export interface DeliveryRow {
  barcode: string;
  item_code: string;
  name: string;
  size: string | null;
  pcs_per_case: number;
  po: number;
  delivered: number;
  /** delivered − po (≤ 0: the flow only lowers). */
  diff: number;
  full_cases: number;
  remainder: number;
  unit_price: number;
  /** delivered × unit price. */
  amount: number;
}

/** ยอดส่งต่อสินค้า — one row per SKU of the PO, in 7-11 item-code order. */
export const buildDeliveryRows = (detail: BdcShipmentDetail): DeliveryRow[] =>
  detail.items
    .map((item) => {
      const delivered = itemDelivered(item);
      const per = item.pcs_per_case;
      return {
        barcode: item.barcode,
        item_code: item.item_code,
        name: item.name,
        size: item.size,
        pcs_per_case: per,
        po: item.qty,
        delivered,
        diff: delivered - item.qty,
        full_cases: per > 0 ? Math.floor(delivered / per) : 0,
        remainder: per > 0 ? delivered % per : delivered,
        unit_price: item.unit_price,
        amount: Math.round(delivered * item.unit_price * 100) / 100,
      };
    })
    .sort((a, b) => a.item_code.localeCompare(b.item_code));

export const deliveryTotals = (rows: DeliveryRow[]) =>
  rows.reduce(
    (total, row) => ({
      po: total.po + row.po,
      delivered: total.delivered + row.delivered,
      changed: total.changed + (row.diff !== 0 ? 1 : 0),
    }),
    { po: 0, delivered: 0, changed: 0 },
  );

// ---- Auto (เกลี่ย) ----

/** Read a whole number of pieces typed with or without thousands commas; null when it is not one. */
export const parsePieces = (text: string): number | null => {
  const cleaned = text.replace(/,/g, "").trim();
  if (!/^\d+$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isSafeInteger(value) ? value : null;
};

/**
 * The new delivered total of one product: a whole number, at least 0 and below the current
 * total (the spread only lowers — plan §4 ข้อ 1). Returns the value or the reason it is refused.
 */
export const checkNewTotal = (text: string, current: number): { value: number } | { error: string } => {
  if (text.trim() === "") return { error: "กรอกยอดส่งรวมใหม่" };
  const value = parsePieces(text);
  if (value === null) return { error: "กรอกเป็นจำนวนชิ้นเต็ม ไม่ติดลบ" };
  if (value >= current) return { error: `ต้องน้อยกว่ายอดส่งปัจจุบัน (${current.toLocaleString("en-US")}) — ลดได้อย่างเดียว` };
  return { value };
};

/** The preview's changed stores as the PUT body lines (mode `auto`). */
export const previewToItems = (preview: BdcAdjustPreview): BdcDeliveryItemRequest[] =>
  preview.rows.map((row) => ({ store_code: row.store_code, barcode: preview.barcode, delivery_qty: row.after }));

// ---- Manual (แก้รายร้าน) ----

/** Typed text per `store/barcode`; a key absent = untouched. */
export type DeliveryDraft = Record<string, string>;

export const draftKey = (storeCode: string, barcode: string) => `${storeCode}/${barcode}`;

/** A typed delivery of one store: a whole number between 0 and the store's PO pieces. */
export const checkStoreQty = (text: string, poQty: number): string | null => {
  const value = parsePieces(text);
  if (value === null) return "จำนวนเต็ม";
  if (value > poQty) return `ไม่เกิน ${poQty}`;
  return null;
};

export interface ManualRow {
  key: string;
  store_code: string;
  store_name: string;
  line_no: BdcShipmentStore["line_no"];
  stop_no: BdcShipmentStore["stop_no"];
  po: number;
  delivered: number;
}

/** The stores that ordered one product, in route order, narrowed by สาย (exact) / code / name. */
export const manualRows = (stores: BdcShipmentStore[], barcode: string, query = ""): ManualRow[] => {
  const q = query.trim();
  return stores
    .filter((store) => (store.qty?.[barcode] ?? 0) > 0)
    .filter(
      (store) =>
        !q ||
        lineText(store.line_no) === lineText(q) ||
        store.store_code.includes(q) ||
        store.store_name.includes(q),
    )
    .sort((a, b) => compareLineNo(a.line_no, b.line_no) || compareLineNo(a.stop_no, b.stop_no))
    .map((store) => ({
      key: draftKey(store.store_code, barcode),
      store_code: store.store_code,
      store_name: store.store_name,
      line_no: store.line_no,
      stop_no: store.stop_no,
      po: store.qty?.[barcode] ?? 0,
      delivered: deliveredQty(store, barcode),
    }));
};

const splitKey = (key: string) => {
  const at = key.lastIndexOf("/");
  return { store_code: key.slice(0, at), barcode: key.slice(at + 1) };
};

/** Every invalid draft cell, by key — the save stays off while any is left. */
export const draftErrors = (draft: DeliveryDraft, stores: BdcShipmentStore[]): Record<string, string> => {
  const byCode = new Map(stores.map((store) => [store.store_code, store]));
  const errors: Record<string, string> = {};
  Object.entries(draft).forEach(([key, text]) => {
    const { store_code, barcode } = splitKey(key);
    const store = byCode.get(store_code);
    const error = store ? checkStoreQty(text, store.qty?.[barcode] ?? 0) : "ไม่พบร้าน";
    if (error) errors[key] = error;
  });
  return errors;
};

/**
 * The PUT body lines of a manual edit: only cells whose typed value differs from what is
 * delivered now (an unchanged pair is neither sent nor logged). Invalid cells are skipped —
 * the caller blocks the save while `draftErrors` is not empty.
 */
export const manualItems = (draft: DeliveryDraft, stores: BdcShipmentStore[]): BdcDeliveryItemRequest[] => {
  const byCode = new Map(stores.map((store) => [store.store_code, store]));
  const items: BdcDeliveryItemRequest[] = [];
  Object.entries(draft).forEach(([key, text]) => {
    const { store_code, barcode } = splitKey(key);
    const store = byCode.get(store_code);
    const value = parsePieces(text);
    if (!store || value === null || value > (store.qty?.[barcode] ?? 0)) return;
    if (value !== deliveredQty(store, barcode)) items.push({ store_code, barcode, delivery_qty: value });
  });
  return items.sort((a, b) => a.barcode.localeCompare(b.barcode) || a.store_code.localeCompare(b.store_code));
};
